import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import { SheetFooter } from "@hortis/ui/sheet";
import { toast } from "@hortis/ui/sonner";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AccessionAutocomplete } from "src/components/accession-autocomplete";
import type { PositionZodInput } from "src/features/create-accession/components/add-plant-material-form/schema";
import { useCollectionSiteHrefs } from "src/utils/hooks/hrefs";
import { usePlaceBasic } from "src/utils/hooks/place";
import { useScrollToError } from "src/utils/hooks/scroll-to-error";
import type {
  MaterialFieldsFragment,
  MaterialQualifierFormat,
} from "../../../generated/graphql";
import {
  MaterialGroup,
  Period,
  PlantMaterialStatus,
  useCreateMaterialMutation,
  useGetAccessionQuery,
  useGetCollectionSiteFromNamesQuery,
} from "../../../generated/graphql";
import type { Nullable } from "../../types/nullable";
import { materialErrorMap } from "../create-accession/components/add-plant-material-form/error-map";
import { PlantMaterialDataFields } from "../create-accession/components/add-plant-material-form/plant-material-data-fields";
import { processPlantMaterialFormData } from "../create-accession/utils/process-form-data";
import { composeMaterialQualifier } from "../records/accession/components/edit-drawer/add-plant-material";
import { onCreateMaterialSuccess } from "./on-create-material-success";
import {
  type NewMaterialSchema,
  type NewMaterialSchemaInput,
  newMaterialSchema,
} from "./schema";

interface InitialValues {
  position?: PositionZodInput;
}

const createDefaultValues = (
  values?: InitialValues,
): Nullable<NewMaterialSchemaInput> => ({
  materialQualifier: undefined,
  firstPresent: new Date(),
  firstAbsent: null,
  materialGroup: MaterialGroup.Plant,
  quantity: null,
  weight: null,
  status: PlantMaterialStatus.Present,
  sex: null,
  tagNumber: null,
  notesData: null,
  massPlanting: false,
  position: values?.position ?? null,
  collectionSiteLocationOption: null,
  lastObserved: new Date(),
  followUp: { count: 1, period: Period.Years },
  condition: null,
  tags: [],
  images: [],
  accessionOption: null,
});

const resolver = zodResolver(newMaterialSchema, { errorMap: materialErrorMap });

export type NewMaterialFormProps = {
  site?: { materialQualifierFormat: MaterialQualifierFormat };
  initialValues?: InitialValues;
  onSubmit?: (newMaterial: MaterialFieldsFragment) => void;
  onDiscard?: () => void;
};

export const NewMaterialForm = ({
  site: _site,
  initialValues,
  onSubmit,
  onDiscard,
}: NewMaterialFormProps) => {
  const navigate = useNavigate();
  const place = usePlaceBasic();
  const hrefs = useCollectionSiteHrefs();

  const [{ data: siteData }] = useGetCollectionSiteFromNamesQuery({
    variables: {
      organisationSubdomain: place?.orgName ?? "",
      collectionSiteSlug: place?.siteSlug ?? "",
    },
    pause: place == null && _site != null,
  });
  const site = _site ?? siteData?.org?.site;
  const formMethods = useForm<Nullable<NewMaterialSchemaInput>>({
    defaultValues: createDefaultValues(initialValues),
    criteriaMode: "all",
    resolver,
  });
  const accessionOption = formMethods.watch("accessionOption");

  const [{ data: accessionData }] = useGetAccessionQuery({
    variables: {
      accessionNumber: accessionOption?.accessionNumber ?? "",
      collectionSiteSlug: place?.siteSlug ?? "",
      organisationSubdomain: place?.orgName ?? "",
    },
    pause: accessionOption == null || place == null,
  });
  const accession = accessionData?.org?.site?.result;
  const [, createMaterial] = useCreateMaterialMutation();

  const { onError } = useScrollToError(formMethods);

  const handleSubmit = useMemo(
    () =>
      formMethods.handleSubmit(async (_data) => {
        try {
          const { accessionOption, ...data } = _data as NewMaterialSchema;
          const res = await createMaterial({
            accessionId: accessionOption.id,
            plantMaterial: processPlantMaterialFormData(data),
          });
          if (res.data?.createPlantMaterial?.__typename === "PlantMaterial") {
            onCreateMaterialSuccess({
              navigate,
              materialsHref: hrefs?.plantMaterials,
              onwardHref: undefined,
            })({
              accessionNumber: accessionOption.accessionNumber,
              qualifier: res.data.createPlantMaterial.qualifier,
            });
            if (onSubmit != null) {
              onSubmit(res.data.createPlantMaterial);
            }
          } else {
            throw new Error("Failed to create material");
          }
        } catch {
          toast.error("Failed to create material");
        }
      }, onError),
    [
      formMethods,
      onError,
      createMaterial,
      navigate,
      hrefs?.plantMaterials,
      onSubmit,
    ],
  );

  useEffect(() => {
    if (accession != null && site != null) {
      formMethods.setValue(
        "materialQualifier",
        composeMaterialQualifier(accession, site.materialQualifierFormat),
      );
    }
  }, [accession, formMethods, site]);

  useEffect(() => {
    if (
      accessionOption == null &&
      formMethods.getValues("materialQualifier") != null
    ) {
      formMethods.setValue("materialQualifier", undefined);
    }
  }, [accessionOption, formMethods]);

  return (
    <FormProvider {...formMethods}>
      <form
        className="flex flex-col gap-12"
        onSubmit={handleSubmit}
        data-cy="new-material-form"
      >
        <div className="flex flex-col gap-8">
          <AccessionAutocomplete
            name="accessionOption"
            testId="accession-option"
          />
          <div className="pt-2">
            <PlantMaterialDataFields formNamePrefix="" />
          </div>
        </div>
        <SheetFooter className="px-0!">
          <Button onClick={onDiscard} testId="cancel-new-material-button">
            Discard
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={formMethods.formState.isSubmitting}
            testId="create-material-button"
          >
            Save
          </Button>
        </SheetFooter>
      </form>
    </FormProvider>
  );
};
